import React, { useEffect, useState } from "react";
import { generatePath, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  CircularProgress,
  Collapse,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  LiveTv as LiveTvIcon,
  PlayCircle as PlayIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { isLocalhost } from "../../utils/isLocalhost";
import { ROUTES_NAMES } from "../../components/routes";
import { useExplorerDates } from "./useExplorerDates";
import { ProgramDetailEpisodeTableRowSkeleton } from "./ExplorerLoadingSkeletons";
import { ExplorerDetailTableHeaderLabel } from "./ExplorerDetailTableHeaderLabel";
import {
  explorerFilterControlSx,
  formatExplorerDurationSeconds,
  formatExplorerTableDateLine,
  formatExplorerTableTimeLine,
  formatNumber,
} from "./utils";
import { apiService } from "../../services/api";
import { EpisodeTitleWithHistory } from "./EpisodeTitleWithHistory";
import { ContentDailyMetricsGrowthSection } from "../../components/content-dialog/ContentDailyMetricsGrowthSection";
import type { DailyMetricPoint } from "../ProgramAnalysis/dailyMetricsChart";

const TABLE_COL_COUNT = 8;
const TABLE_ACTIONS_COL_WIDTH = 168;
const ROWS_PER_PAGE_OPTIONS = [5, 10, 20];
const SEARCH_DEBOUNCE_MS = 300;

export type PodcastEpisodeProgramOption = {
  _id: string;
  name: string;
};

function formatMetricValue(value?: number | null): string {
  const v = Number(value) || 0;
  if (v <= 0) return "—";
  return formatNumber(v);
}

function getYouTubeUrl(episode: {
  url?: string;
  youtubeId?: string;
}): string | null {
  if (episode.url?.trim()) return episode.url.trim();
  if (episode.youtubeId?.trim()) {
    return `https://www.youtube.com/watch?v=${episode.youtubeId.trim()}`;
  }
  return null;
}

const EpisodeGrowthDetails: React.FC<{ contentId: string }> = ({
  contentId,
}) => {
  const { t } = useTranslation("explorer");
  const { data, isLoading, isError } = useQuery({
    queryKey: ["content-daily-metrics", contentId],
    queryFn: () => apiService.getContentDailyMetrics(contentId),
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (isError || !data) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t("metricsLoadError")}
      </Typography>
    );
  }

  if (!data.dailyMetrics?.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t("noDailyMetricsEpisode")}
      </Typography>
    );
  }

  const dailyMetrics: DailyMetricPoint[] = data.dailyMetrics.map((m) => ({
    collectedAt:
      typeof m.collectedAt === "string"
        ? m.collectedAt
        : m.collectedAt.toISOString(),
    daysSincePublished: m.daysSincePublished,
    publishedAt: data.publishedAt,
    views: m.views,
    likes: m.likes,
    comments: m.comments,
  }));

  return (
    <ContentDailyMetricsGrowthSection
      contentId={contentId}
      dailyMetrics={dailyMetrics}
    />
  );
};

export type PodcastDetailEpisodesTableProps = {
  podcastId: string;
  startDate?: string;
  endDate?: string;
  programs: PodcastEpisodeProgramOption[];
};

export const PodcastDetailEpisodesTable: React.FC<
  PodcastDetailEpisodesTableProps
> = ({ podcastId, startDate, endDate, programs }) => {
  const { t } = useTranslation("explorer");
  const navigate = useNavigate();
  const { withDates } = useExplorerDates();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [copiedEpisodeId, setCopiedEpisodeId] = useState<string | null>(null);
  const showCopyEpisodeId = isLocalhost();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [programFilter, setProgramFilter] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timeout);
  }, [searchInput]);

  useEffect(() => {
    setPage(0);
  }, [podcastId, startDate, endDate, programFilter, debouncedSearch]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [
      "explorer-podcast-episodes",
      podcastId,
      startDate,
      endDate,
      programFilter,
      debouncedSearch,
      page,
      rowsPerPage,
    ],
    queryFn: () =>
      apiService.getExplorerPodcastEpisodes(
        podcastId,
        startDate,
        endDate,
        page,
        rowsPerPage,
        {
          programId: programFilter !== "all" ? programFilter : undefined,
          search: debouncedSearch || undefined,
        },
      ),
    enabled: !!podcastId,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const episodes = data?.episodes ?? [];
  const total = data?.total ?? 0;
  const showSkeleton = isLoading && !data;
  const hasFilters = programFilter !== "all" || Boolean(debouncedSearch);
  const showEmptyRow = !showSkeleton && total === 0;

  useEffect(() => {
    if (!data) return;
    const maxPage = Math.max(0, Math.ceil(data.total / rowsPerPage) - 1);
    if (page > maxPage) setPage(maxPage);
  }, [data, page, rowsPerPage]);

  const handleCopyEpisodeId = (episodeId: string) => {
    navigator.clipboard.writeText(episodeId).then(() => {
      setCopiedEpisodeId(episodeId);
      window.setTimeout(() => setCopiedEpisodeId(null), 2000);
    });
  };

  return (
    <Box>
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        useFlexGap
        sx={{ mb: 2 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          {showSkeleton ? (
            <Skeleton variant="text" width={220} height={36} />
          ) : (
            <Typography variant="h2" color="text.primary">
              {t("recentEpisodes", { count: total })}
            </Typography>
          )}
          {isFetching && !showSkeleton && <CircularProgress size={16} />}
        </Stack>
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ minWidth: 0, flex: { xs: "1 1 100%", md: "0 1 auto" } }}
        >
          <FormControl size="small" sx={explorerFilterControlSx(200)}>
            <InputLabel>{t("program")}</InputLabel>
            <Select
              label={t("program")}
              value={programFilter}
              onChange={(e) => setProgramFilter(e.target.value)}
            >
              <MenuItem value="all">{t("allPrograms")}</MenuItem>
              {programs.map((program) => (
                <MenuItem key={program._id} value={program._id}>
                  {program.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            label={t("search")}
            placeholder={t("searchByEpisode")}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{
              ...explorerFilterControlSx(240),
              minWidth: { xs: "100%", sm: 200 },
              maxWidth: { sm: 320 },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
              endAdornment: searchInput ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label={t("clearSearch")}
                    edge="end"
                    onClick={() => setSearchInput("")}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
        </Stack>
      </Stack>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="left" sx={{ width: 108, whiteSpace: "nowrap" }}>
                <ExplorerDetailTableHeaderLabel
                  label={t("date")}
                  tooltip={t("tooltip.episodePublishedAt")}
                />
              </TableCell>
              <TableCell align="left" sx={{ minWidth: 320 }}>
                <ExplorerDetailTableHeaderLabel
                  label={t("title")}
                  tooltip={t("tooltip.episodeName")}
                />
              </TableCell>
              <TableCell align="left" sx={{ minWidth: 180 }}>
                <ExplorerDetailTableHeaderLabel
                  label={t("program")}
                  tooltip={t("tooltip.episodeProgram")}
                />
              </TableCell>
              <TableCell align="right">
                <ExplorerDetailTableHeaderLabel
                  label={t("duration")}
                  tooltip={t("tooltip.episodeDuration")}
                  align="right"
                />
              </TableCell>
              <TableCell align="right">
                <ExplorerDetailTableHeaderLabel
                  label={t("views")}
                  tooltip={t("tooltip.episodeViews")}
                  align="right"
                />
              </TableCell>
              <TableCell align="right">
                <ExplorerDetailTableHeaderLabel
                  label={t("likes")}
                  tooltip={t("tooltip.episodeLikes")}
                  align="right"
                />
              </TableCell>
              <TableCell align="right">
                <ExplorerDetailTableHeaderLabel
                  label={t("comments")}
                  tooltip={t("tooltip.episodeComments")}
                  align="right"
                />
              </TableCell>
              <TableCell
                align="right"
                sx={{ width: TABLE_ACTIONS_COL_WIDTH, px: 0.5 }}
              />
            </TableRow>
          </TableHead>
          <TableBody>
            {showSkeleton ? (
              Array.from({ length: rowsPerPage }, (_, i) => (
                <ProgramDetailEpisodeTableRowSkeleton
                  key={`podcast-episode-skeleton-${i}`}
                  showProgram
                />
              ))
            ) : showEmptyRow ? (
              <TableRow>
                <TableCell
                  colSpan={TABLE_COL_COUNT}
                  align="center"
                  sx={{ py: 4, color: "text.secondary" }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {hasFilters ? t("noEpisodesMatchSearch") : t("noEpisodes")}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              episodes.map((episode) => {
                const youtubeUrl = getYouTubeUrl(episode);
                const isExpanded = expandedId === episode._id;
                return (
                  <React.Fragment key={episode._id}>
                    <TableRow
                      hover
                      onClick={(e) => {
                        e.preventDefault();
                        setExpandedId((prev) =>
                          prev === episode._id ? null : episode._id,
                        );
                      }}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell
                        align="left"
                        sx={{ width: 108, whiteSpace: "nowrap" }}
                      >
                        <Stack spacing={0.25}>
                          <Typography variant="body2" color="text.primary">
                            {formatExplorerTableDateLine(episode.publishedAt)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {formatExplorerTableTimeLine(episode.publishedAt)}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell align="left" sx={{ minWidth: 320, whiteSpace: "normal" }}>
                        <EpisodeTitleWithHistory
                          title={episode.title}
                          titleHistory={episode.titleHistory}
                        />
                      </TableCell>
                      <TableCell align="left" sx={{ minWidth: 180, whiteSpace: "normal" }}>
                        <Typography
                          variant="body2"
                          color={
                            episode.programName
                              ? "text.primary"
                              : "text.secondary"
                          }
                          sx={{ whiteSpace: "normal", overflowWrap: "break-word" }}
                        >
                          {episode.programName || t("unassignedProgram")}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.primary">
                          {formatExplorerDurationSeconds(episode.duration)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.primary">
                          {formatMetricValue(episode.views)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.primary">
                          {formatMetricValue(episode.likes)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.primary">
                          {formatMetricValue(episode.comments)}
                        </Typography>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ width: TABLE_ACTIONS_COL_WIDTH, px: 0.5 }}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="flex-end"
                          spacing={0}
                          sx={{ flexShrink: 0 }}
                        >
                          {showCopyEpisodeId ? (
                            <Tooltip
                              title={
                                copiedEpisodeId === episode._id
                                  ? t("copied")
                                  : t("copyEpisodeId")
                              }
                            >
                              <IconButton
                                size="small"
                                aria-label={t("copyEpisodeId")}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCopyEpisodeId(episode._id);
                                }}
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                          {episode.livestream && episode.youtubeId ? (
                            <Tooltip title={t("openLivestreamEmission")}>
                              <IconButton
                                size="small"
                                sx={{ color: "podscope.main" }}
                                aria-label={t("openLivestreamEmission")}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const path = generatePath(
                                    ROUTES_NAMES.EXPLORER_LIVESTREAM_CHANNEL,
                                    { channelId: episode.livestream!.channelId },
                                  );
                                  navigate(
                                    withDates(
                                      `${path}?videoId=${encodeURIComponent(episode.youtubeId!)}`,
                                    ),
                                  );
                                }}
                              >
                                <LiveTvIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                          {youtubeUrl ? (
                            <IconButton
                              size="small"
                              sx={{ color: "error.main" }}
                              aria-label={t("openOnYouTube")}
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(
                                  youtubeUrl,
                                  "_blank",
                                  "noopener,noreferrer",
                                );
                              }}
                            >
                              <PlayIcon />
                            </IconButton>
                          ) : null}
                          <IconButton
                            size="small"
                            aria-label={
                              isExpanded ? t("collapseRow") : t("expandRow")
                            }
                            tabIndex={-1}
                          >
                            {isExpanded ? (
                              <ExpandLessIcon fontSize="small" />
                            ) : (
                              <ExpandMoreIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        colSpan={TABLE_COL_COUNT}
                        sx={{ py: 0, borderBottom: isExpanded ? undefined : 0 }}
                      >
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 2, px: 1 }}>
                            <EpisodeGrowthDetails contentId={episode._id} />
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
        {!showSkeleton && total > 0 ? (
          <TablePagination
            component="div"
            count={total}
            page={Math.min(
              page,
              Math.max(0, Math.ceil(total / rowsPerPage) - 1),
            )}
            onPageChange={(_, next) => setPage(next)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            labelRowsPerPage={t("rowsPerPage")}
            labelDisplayedRows={({ from, to, count }) =>
              t("pagination.range", {
                from,
                to,
                shown: count !== -1 ? count : t("pagination.moreThan", { to }),
              })
            }
          />
        ) : null}
      </TableContainer>
    </Box>
  );
};
